import type { ReactNode } from 'react';
import { cx } from './cx';

/**
 * Controlled tabs / segmented control. One primitive for the two patterns the
 * app keeps hand-rolling:
 *   variant="underline"  - section nav (Admin sections, page sub-nav)
 *   variant="segmented"  - a pill-in-track toggle (SignIn email/password,
 *                          Admin feedback filters)
 * Same selection model for both so call sites are interchangeable.
 */
export type TabItem<T extends string = string> = {
  value: T;
  label: ReactNode;
};

export function Tabs<T extends string = string>({
  items,
  value,
  onChange,
  variant = 'underline',
  className,
  'aria-label': ariaLabel,
}: {
  items: Array<TabItem<T>>;
  value: T;
  onChange: (value: T) => void;
  variant?: 'underline' | 'segmented';
  className?: string;
  'aria-label'?: string;
}) {
  if (variant === 'segmented') {
    return (
      <div
        role="tablist"
        aria-label={ariaLabel}
        className={cx('inline-flex gap-1 rounded-sm border border-border bg-bone p-1', className)}
      >
        {items.map((item) => {
          const on = item.value === value;
          return (
            <button
              key={item.value}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => onChange(item.value)}
              className={cx(
                'rounded-[3px] px-3 py-1.5 font-body text-caption font-semibold leading-none',
                'transition-[background-color,color] duration-150',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
                on
                  ? 'bg-surface text-ink shadow-[var(--shadow-soft)]'
                  : 'text-text-muted hover:text-text',
              )}
            >
              {item.label}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cx('flex flex-wrap gap-5 border-b border-border', className)}
    >
      {items.map((item) => {
        const on = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(item.value)}
            className={cx(
              '-mb-px border-b-2 pb-2 font-body text-small font-semibold leading-none',
              'transition-[color,border-color] duration-150',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
              on
                ? 'border-ink text-ink'
                : 'border-transparent text-text-muted hover:text-text',
            )}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
